import { useCallback, useState } from 'react';
import { PrefsScope, prefsStorageKey, readPrefsValue, writePrefsValue } from './prefsStorage';

export function viewStorageKey(resourceKey: string, scope: PrefsScope): string {
  return prefsStorageKey('crud.view', resourceKey, scope);
}

/**
 * The stored view for this resource and tenant, reconciled against the views the config
 * currently offers. Never throws — the same contract readColumnPrefs holds, for the same reasons.
 */
export function readViewPref(resourceKey: string, viewKeys: string[], scope: PrefsScope): string {
  const raw = readPrefsValue('crud.view', resourceKey, scope);
  return raw !== null && viewKeys.includes(raw) ? raw : viewKeys[0];
}

export function writeViewPref(resourceKey: string, view: string, scope: PrefsScope): void {
  writePrefsValue('crud.view', resourceKey, scope, view);
}

/**
 * Keyed by resource AND tenant, not by route: the four Issues tabs share one setting — they are
 * one resource seen through four filters — while a different restaurant gets its own.
 */
export function useViewPref(
  resourceKey: string,
  viewKeys: string[],
  scope: PrefsScope
): [string, (next: string) => void] {
  const [stored, setStored] = useState(() => readViewPref(resourceKey, viewKeys, scope));
  // State, not a ref, for the reason useColumnPrefs gives: a restaurant switch or a tabbed layout
  // over two resources re-reads before painting, and a ref written during render tears under
  // concurrent rendering.
  const identity = `${scope.orgSlug}/${scope.restaurantId}/${resourceKey}`;
  const [lastIdentity, setLastIdentity] = useState(identity);
  if (lastIdentity !== identity) {
    setLastIdentity(identity);
    setStored(readViewPref(resourceKey, viewKeys, scope));
  }

  const active = viewKeys.includes(stored) ? stored : viewKeys[0];
  // The caller re-creates the scope object every render; depending on its two primitives keeps
  // `set` stable without changing what gets written.
  const { orgSlug, restaurantId } = scope;
  const set = useCallback(
    (next: string) => {
      setStored(next);
      writeViewPref(resourceKey, next, { orgSlug, restaurantId });
    },
    [resourceKey, orgSlug, restaurantId]
  );
  return [active, set];
}
